// Copyright (c) Docugami, Inc. All rights reserved.

import { ChangeEvent } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { adjustColor } from 'utils/ColorUtils';
import { aaTextColor, primaryTheme, emptyColor, secondaryTheme, primaryColorDark, themeBlueButtonDisabled, themeBlue, themeGreenButtonDisabled, themeRed, themeRedButtonDisabled, ChunkNavigationControlColor, emptyGray } from '../shell/presentation/AppStyles';
import { dgFontFamily } from './Fonts';

/**
 * Props for styled components that can have a selected state
 */
export interface DgSelectable {
    readonly selected: boolean
}

/**
 * Props for styled components that can have both a selected
 * and hovered state
 */
export interface DgSelectableAndHoverable extends DgSelectable {
    readonly hovered: boolean
}

/**
 * Style span, single line with ellipsis
 */
export const DgSingleLineEllipsis = styled.span`
    // ellipsis for singleline
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export interface DgMultineEllipsisProps {
    numLines: number
}

/**
 * Styled span that limits number of lines rendered to numLines with
 * an ellipsis
 */
export const DgMultineEllipsis = styled.span<DgMultineEllipsisProps>`
    // ellipsis suppport for multiline
    display: -webkit-box;
    -webkit-line-clamp: ${props => props.numLines};
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

/**
 * Styled span that will not allow text selection
 */
export const DgNonSelectableLabelCss = css`
    user-select: none;
`;

export interface DgVerticalSpacerProps {
    readonly height: string
}

/**
 * Styled div for vertical spacing: just assign a height :)
 */
export const DgVerticalSpacer = styled.div<DgVerticalSpacerProps>`
    height: ${props => props.height};
`;

export interface DgHorizontalSpacerProps {
    readonly width: string
}

/**
 * Styled div for horizontal spacing: just assign a width :)
 */
export const DgHorizontalSpacer = styled.div<DgHorizontalSpacerProps>`
    width: ${props => props.width};
`;

/**
 * Styled div with flex column layout and 100% height
 */
export const DgFlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

/**
* Styled div with flex column layout, 100% height and center
* aligned content
*/
export const DgFlexCenteredColumn = styled(DgFlexColumn)`
    align-items: center;
`;

/**
 * Styled div with flex row layout and 100% width
 */
export const DgFlexRow = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
`;

/**
* Styled div with flex row layout, 100% width and center
* aligned content
*/
export const DgFlexCenteredRow = styled(DgFlexRow)`
    align-items: center;
`;

/**
 * Div spacer with Flex=1
 */
export const DgFlexSpacer = styled.div`
    flex: 1;
`;

/**
 * Styled Docugami button
 */
export const DgButton = styled.button`
    font-family: ${dgFontFamily};
    font-style: 'normal';
    font-weight: 'normal';
    font-size: 16px;
    color: ${(props: any) => props.disabled ? aaTextColor : primaryTheme.palette.white};
    background: ${(props: any) => props.disabled ? emptyColor : primaryTheme.palette.themePrimary};
    border-radius: 5px;
    padding: 8px 13px;
    cursor: pointer;
    border-width: 0px;
    ${props => props.disabled && DisabledCss}

    &:hover {
        background-color: ${(props: any) => props.disabled ? '' : secondaryTheme.palette.themeDarkAlt};
    }

    &:active:hover {
        background-color: ${primaryColorDark};
    }
`;

export const DefaultFontButtonStateColors: ButtonStateColors = {
    color: primaryTheme.palette.white,
    disabled: primaryTheme.palette.white,
    hover: primaryTheme.palette.white,
    down: primaryTheme.palette.white
}

export const TransparentButtonStateColors: ButtonStateColors = {
    color: 'transparent',
    disabled: 'transparent',
    hover: 'transparent',
    down: 'transparent'
}

export const DgRedColoredButtonProps: DgColoredButtonProps = {
    background: {
        color: themeRed,
        disabled: themeRedButtonDisabled,
        hover: adjustColor(themeRed, -20),
        down: adjustColor(themeRed, -20)
    },

    font: DefaultFontButtonStateColors
}

export const DgMagentaColoredButtonProps: DgColoredButtonProps = {
    background: {
        color: ChunkNavigationControlColor,
        disabled: ChunkNavigationControlColor,
        hover: adjustColor(ChunkNavigationControlColor, -20),
        down: ChunkNavigationControlColor
    },

    font: DefaultFontButtonStateColors
}

export const DgGreenColoredButtonProps: DgColoredButtonProps = {
    background: {
        color: primaryTheme.palette.themePrimary,
        disabled: themeGreenButtonDisabled,
        hover: adjustColor(primaryTheme.palette.themePrimary, -20),
        down: adjustColor(primaryTheme.palette.themePrimary, -20)
    },

    font: DefaultFontButtonStateColors
}

export const DgBlueColoredButtonProps: DgColoredButtonProps = {
    background: {
        color: themeBlue,
        disabled: themeBlueButtonDisabled,
        hover: adjustColor(themeBlue, -20),
        down: adjustColor(themeBlue, -20)
    },
    font: DefaultFontButtonStateColors
}

export interface DgColoredButtonProps {
    background: ButtonStateColors;
    font: ButtonStateColors;
};

export interface ButtonStateColors {
    color: string;
    disabled: string;
    hover: string;
    down: string;
}

/**
 * Customizable colored flex button. You should customize padding, size, flex settings, etc.
 * however you like.
 */
export const DgColoredButton = styled.button<{ controlButtonProps: DgColoredButtonProps }>`
    font-family: ${dgFontFamily};
    font-style: 'normal';
    font-weight: 'normal';
    font-size: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
    border: none;
    padding: 0;
    background: ${props => props.disabled ? props.controlButtonProps.background.disabled : props.controlButtonProps.background.color};
    color: ${props => props.disabled ? props.controlButtonProps.font.disabled : props.controlButtonProps.font.color};
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    &:hover {
        background-color: ${props => props.disabled ? props.controlButtonProps.background.disabled : props.controlButtonProps.background.hover};
        color: ${props => props.disabled ? props.controlButtonProps.font.disabled : props.controlButtonProps.font.hover};
    }
    &:active:hover {
        background-color: ${props => props.disabled ? props.controlButtonProps.background.disabled : props.controlButtonProps.background.down};
        color: ${props => props.disabled ? props.controlButtonProps.font.disabled : props.controlButtonProps.font.down};
    }
`;

/**
 * Styled span, loading ellipses with step animation
 */
const ellipsesAnimation = keyframes`
    to {
        width: 1.3em;
    }
`;

export const Ellipses = styled.span<{ hidden: boolean, speed?: string }>`
    display: ${props => props.hidden ? 'none' : 'inline-block'};
    width: 15px;
    text-align: left;

    :after {
        overflow: hidden;
        display: inline-block;
        vertical-align: bottom;
        content: "\u2026";
        animation: ${ellipsesAnimation} steps(4,end) infinite ${props => props.speed ?? '900ms'};
        width: 1px;
    }
`;

/**
 * Styled radio button
 */
const RadioWrapper = styled.div`
    display: inline-block;
`;

const Mark = styled.span`
    display: inline-block;
    position: relative;
    border: 1px solid #777777;
    width: 20px;
    height: 20px;
    left: 0;
    border-radius: 50%;
    margin-right: 5px;
    vertical-align: middle;

    &:hover {
        &::after {
            width: 14px;
            height: 14px;
            opacity: .6;
            left: 13%;
            top: 13%;
        }
    }

    &::after {
        content: "";
        display: block;
        width: 0;
        height: 0;
        border-radius: 50%;
        background-color: ${primaryTheme.palette.themePrimary};
        opacity: 0;
        left: 50%;
        top: 50%;
        position: absolute;
        transition: all 110ms;
    }
`;

const Input = styled.input`
    position: absolute;
    visibility: hidden;
    display: none;
    &:checked + ${Mark} {
        &::after {
        width: 14px;
        height: 14px;
        opacity: 1;
        left: 13%;
        top: 13%;
        }
    }
`;

const Label = styled.label`
    display: flex;
    cursor: pointer;
    padding: 5px 10px 5px 0;
    position: relative;
`;

export const DgInputBox = styled.input`
    border-top-style: hidden;
    border-right-style: hidden;
    border-left-style: hidden;
    background: transparent;
    min-width: 60.6%;
    padding: 5px;
    outline: none;
    margin-top: 20px;
    display: block;
    border-color: black;
    font-family: ${dgFontFamily};
    font-size: 48px;
    font-weight: 700;
    font-style: normal;
    ::placeholder {
        color: ${emptyGray};
        font-style: italic;
    }
`;

export interface RadioProps {
    value: string;
    children: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    checked: boolean;
}

export const Radio = (props: RadioProps) => (
    <RadioWrapper>
        <Label>
            <Input value={props.value} type="radio" onChange={props.onChange} checked={props.checked} />
            <Mark />
            {props.children}
        </Label>
    </RadioWrapper>
);

export const DisabledCss = css`
    opacity: .5;
    cursor: default;
    pointer-events: none;
`

export const LightDisabledCss = css`
    opacity: .3;
    cursor: default;
    pointer-events: none;
`;
